import { useState, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ForgotPassword from './ForgotPassword';
import './Login.css';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const { login } = useAuth();

  if (showForgotPassword) {
    return <ForgotPassword onBack={() => setShowForgotPassword(false)} />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password, rememberMe);
      // Navigation will happen automatically via auth context
    } catch (err: any) {
      // Extract user-friendly message from Convex error
      const errorMessage = err.message || 'Login failed';

      // Clean up technical details (stack traces, request IDs, etc.)
      const cleanMessage = errorMessage
        .split('\n')[0]  // Take only first line
        .replace(/\[CONVEX.*?\]/g, '')  // Remove Convex metadata
        .replace(/\[.*?Request ID.*?\]/gi, '') // Remove request IDs
        .replace(/\[[^\]]*\]/g, '') // Remove any remaining bracketed tags
        .replace(/at handler.*$/g, '')  // Remove stack trace info
        .replace(/Called by client.*$/g, '')  // Remove call info
        .replace(/Uncaught Error:\s*/g, '')  // Remove "Uncaught Error:"
        .replace(/Server Error\s*/g, '')  // Remove "Server Error"
        .trim();

      // Normalize common auth message
      const friendly = /invalid email or password/i.test(cleanMessage)
        ? 'Invalid email or password'
        : cleanMessage;

      setError(friendly || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>🏴‍☠️ Piracy Detection System</h1>
          <p>Internal Access Only</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              ⚠️ {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="your.email@company.com"
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="••••••••"
              disabled={isLoading}
            />
          </div>

          <div className="form-group-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={isLoading}
              />
              <span>Keep me logged in (30 days)</span>
            </label>
          </div>

          <button
            type="submit"
            className="btn-login"
            disabled={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>

          <button
            type="button"
            className="btn-forgot-password"
            onClick={() => setShowForgotPassword(true)}
            disabled={isLoading}
          >
            Forgot password?
          </button>
        </form>

        <div className="login-footer">
          <p>Contact your administrator for access</p>
        </div>
      </div>
    </div>
  );
}
